import { useEffect, useMemo, useState } from "react";
import { Settings } from "lucide-react";
import {
  Button, IconButton, Wordmark, BrandMark, ProBadge, EmptyState, Spinner, ShellNav, ShellTopbar,
  ShellTopbarActions, useMinWidth, type ShellNavItem,
} from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useMe, useNavigation } from "../../app/hooks/useSession.js";
import { hasInbox } from "../../app/auth/session.js";
import { InboxButton } from "../../features/inbox/InboxButton.js";
import { hasBookingPage, useArtistShowLink } from "../../app/showLink.js";
import { NotificationBell } from "../../features/notifications/NotificationBell.js";
import { FixtureSwitchButton } from "../../features/dev/FixtureSwitchButton.js";
import { FixtureSwitchDialog } from "../../features/dev/FixtureSwitchDialog.js";
import { SettingsModal } from "../../features/settings/SettingsModal.js";
import type { SettingsTab } from "../../features/settings/settingsTabs.js";
import { SETTINGS_PARAM } from "../../app/oauthPopup.js";
import { useSettingsPanel } from "../../app/settingsPanel.js";
import { useResolvedActiveBandId } from "../../app/activeBand.js";
import { BandProvider, bandContextFromNav, isProContext } from "../../app/bandContext.js";
import { useLiveGroup } from "../../app/realtime/RealtimeProvider.js";
import { useAuth } from "../../app/auth/useAuth.js";
import { BandSwitcher } from "./BandSwitcher.js";
import { MyProfileModal } from "../../features/contacts/MyProfileModal.js";
import { ProfileModal } from "../../features/profile/ProfileModal.js";
import styles from "./ArtistShell.module.scss";

type MeDocument = components["schemas"]["MeDocument"];
type NavigationSurface = components["schemas"]["NavigationSurface"];

/** A rendered nav entry (CD nav-ia 2026-07-27). `value` is the destination ROUTE on this shell. */
type NavItem = ShellNavItem;

/**
 * The grouped desktop IA (CD TopNav, nav-ia 2026-07-27): Dashboard · Music▾ · Content▾ · Touring▾ ·
 * Antenna · Contacts. Purely PRESENTATIONAL layout over the server capability document — a leaf renders
 * iff its surface is server-visible (nav-as-data; the client still holds no role→tab matrix), and a
 * group with zero surviving children is dropped entirely, never rendered empty. Group labels are
 * <button>s that only open the menu — they never navigate.
 *
 * Layout decisions (vs. the flat surface list):
 * - Exactly one of Booking/Calendar renders, never both: the server `money` surface visibility forks the
 *   `schedule` leaf's route AND label (the capabilities-as-data mirror of CD canSeeBooking). Mobile
 *   deliberately keeps the label "Schedule" — the rename is desktop-only.
 * - The Assets leaf under Content is the Core+EPK page and needs `assets:read`: the production crew's
 *   `assets:venue`-only grant makes the assets SURFACE visible but must NOT surface Core+EPK ("production
 *   crew never sees Assets under Content"). Their asset door is Advance Docs under Touring (/advance),
 *   which rides the bare assets surface — one surface, two leaves, two capability audiences.
 * - Surfaces with no place in the IA render NOTHING (never a dead `#hash` anchor): `money` (a fork
 *   signal, not a tab), `guests` (the Booking cockpit owns per-show guest lists), `connections` (a
 *   Settings tab), and `friends`/`roster`/`admin` (no destination screen yet).
 */
function buildNavItems(
  surfaces: NavigationSurface[],
  caps: Record<string, boolean | undefined>,
  isPro: boolean,
): NavItem[] {
  const byKey = new Map(surfaces.map((s) => [s.key ?? "", s]));
  const moneyVisible = hasBookingPage(caps);
  const leaf = (key: string, route: string, label?: string, requiresCap?: string) => {
    const surface = byKey.get(key);
    if (!surface || (requiresCap && !caps[requiresCap])) return null;
    return { label: label ?? surface.label ?? key, value: route };
  };
  const present = <T,>(xs: (T | null)[]): T[] => xs.filter((x): x is T => x !== null);

  const items: NavItem[] = [{ kind: "leaf", label: "Dashboard", value: "/" }];
  const group = (label: string, children: ({ label: string; value: string } | null)[]) => {
    const kids = present(children);
    if (kids.length > 0) items.push({ kind: "group", label, children: kids });
  };
  group("Music", [leaf("releases", "/releases"), leaf("music", "/music")]);
  group("Content", [
    leaf("assets", "/assets", undefined, "assets:read"),
    leaf("content", "/socials"),
    leaf("marketing", "/marketing"),
  ]);
  group("Touring", [
    // Booking for the money team; the Calendar for everyone with schedule access, under Booking (ART-305).
    moneyVisible ? leaf("schedule", "/booking", "Booking") : null,
    leaf("schedule", "/calendar", "Calendar"),
    // Advance Docs (the production/venue stack) — the assets SURFACE with no cap refinement, so the
    // venue-only crew (assets:venue) reach it here even though Core+EPK Assets under Content is closed
    // to them. Merch (no assets tokens) sees neither. CD: "labeled Advance Docs on both plans".
    leaf("assets", "/advance", "Advance Docs"),
    // Buses (PRO, ART-147) — a Pro-only touring surface with no server nav surface of its own. Gated on
    // Pro AND production:write (the touring-production audience); the SCREEN does the precise AV-or-TM
    // narrowing and renders a designed "your bunk is on your dashboard" state for a non-editor, so this is
    // not a dead 402/403 button. Management reaches artists through the ROSTER shell, which carries no Buses
    // tab, so it never appears there (the per-seat visibility ruling).
    isPro && caps["production:write"] ? { label: "Buses", value: "/buses" } : null,
  ]);
  for (const [key, route] of [
    ["antenna", "/antenna"],
    ["contacts", "/contacts"],
  ] as const) {
    const l = leaf(key, route);
    if (l) items.push({ kind: "leaf", ...l });
  }
  return items;
}

/**
 * The "Welcome back, <name>" name (CD TopNav, welcome-name 2026-07-17 rule): a BAND identity (the band's own
 * root account — owner seat / `root|role === "artist"`) greets with the FULL band name (splitting "Relient K"
 * to "Relient" mangles the identity); a PERSON (member/crew/manager) greets with their friendly first name.
 */
function welcomeName(me: MeDocument | undefined, activeBandId: string | undefined): string {
  const person = me?.person;
  const firstName = person?.firstName || person?.displayName?.split(/\s+/)[0] || "";
  const seat = me?.seats?.find((s) => s.kind === "band" && s.bandId === activeBandId)
    ?? me?.seats?.find((s) => s.kind === "band");
  // Band identity = the OWNER seat only. `root` is "artist" on EVERY band seat and role "artist" never
  // occurs in this model (the owner is DB-pinned to is_owner + role band_member_av), so keying on either
  // greets every member with the band name instead of their first name.
  const isBandIdentity = seat?.isOwner === true;
  return isBandIdentity ? (seat?.displayName || firstName) : firstName;
}

/** Active-tab match: the ink pill lights when the current route shares a first path segment with the tab route. */
function isTabActive(activeRoute: string | undefined, route: string): boolean {
  if (!activeRoute) return false;
  const seg = (p: string) => "/" + (p.split("/").filter(Boolean)[0] ?? "");
  if (route === "/") return activeRoute === "/";
  return seg(activeRoute) === seg(route);
}

export interface ArtistShellProps {
  /** The routed module (dashboard, booking, …). At the platform stage this is the placeholder dashboard. */
  children: React.ReactNode;
  onLogout: () => void;
  /** Navigate to a notification's deep-link when a drawer row is tapped (provided by the router shell). */
  onNavigate?: (route: string) => void;
  /** Current pathname (from the router shell), driving the active-nav ink pill. Mirrors MobileShell.activeRoute. */
  activeRoute?: string;
}

/**
 * ArtistShell — chrome only, zero feature logic: sticky translucent topbar, the live nav rendered
 * from the SERVER navigation document (capabilities-as-data, — the client holds NO role→tab matrices),
 * header actions, and the band switcher. Wraps the routed module in BandContext. Loading + error
 * states are designed, not blank.
 */
export function ArtistShell({ children, onLogout, onNavigate, activeRoute }: ArtistShellProps) {
  const me = useMe();
  // ONE inbox-badge source: the topbar, the mobile tab-bar, and the app icon all read this same
  // React Query count, so they never disagree; the `thread-updated`/`badge` signals invalidate it live.
  // "View my profile" opens IN PLACE (PO, 2026-08-20) — modal state here, never a route change. The
  // modal itself mounts under <BandProvider> below (its hooks are band-scoped); the no-band session
  // falls back to the plain person-record editor outside it.
  const [profileOpen, setProfileOpen] = useState(false);
  const [fixtureSwitchOpen, setFixtureSwitchOpen] = useState(false);
  // `?settings=connections` (or `account`) reopens Settings on that tab. Set by the OAuth FALLBACK return path when a
  // popup was blocked: the band is sent back to the page they were on rather than a fixed route, and the
  // modal they were working in comes back with them instead of vanishing.
  const [settingsTab, setSettingsTab] = useState<SettingsTab | null>(() => {
    if (typeof window === "undefined") return null;
    const requested = new URLSearchParams(window.location.search).get(SETTINGS_PARAM);
    // `account` is the per-person Google Calendar connect's fallback (Settings › Account).
    return requested === "connections" || requested === "account" ? requested : null;
  });
  const [settingsOpen, setSettingsOpen] = useState(() => settingsTab !== null);

  // An in-app surface asking for a Settings tab (e.g. the Socials account strip's "Switch account").
  // Separate from the ?settings= param above, which only fires at MOUNT because it serves a cold return
  // from the OAuth fallback; this shell is already mounted when a button in the page asks.
  const requestedTab = useSettingsPanel((s) => s.requestedTab);
  const consumeRequestedTab = useSettingsPanel((s) => s.consume);
  useEffect(() => {
    if (requestedTab === null) return;
    setSettingsTab(requestedTab);
    setSettingsOpen(true);
    consumeRequestedTab();   // a REQUEST, not a second source of truth for "is settings open"
  }, [requestedTab, consumeRequestedTab]);

  // Strip the marker once consumed so a refresh (or a shared URL) does not keep reopening the modal.
  useEffect(() => {
    if (settingsTab === null || typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has(SETTINGS_PARAM)) return;
    url.searchParams.delete(SETTINGS_PARAM);
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, [settingsTab]);
  // the bell opens the DRAWER on desktop and navigates to the full `/notifications` PAGE on mobile.
  const isDesktop = useMinWidth("md");

  // Remembered on this device → starred on the account → first band seat (ART-319; see activeBand.ts).
  const activeBandId = useResolvedActiveBandId();
  const context = activeBandId ? `band:${activeBandId}` : undefined;
  const nav = useNavigation(context);
  const showHref = useArtistShowLink(activeBandId);

  const bandContext = useMemo(
    () => (nav.data ? bandContextFromNav(nav.data, "artist") : null),
    [nav.data],
  );

  // The shell's own groups (router-driven): the person's `user:{id}` group (notifications/badges)
  // and the active `band:{id}` group (leaves the old + joins the new when the band changes — the declarative
  // half of the scorch). Show groups are joined by the guest-list route.
  //
  // personId is LATCHED: switchBand's queryClient.clear() briefly evicts /me, so me.data goes undefined mid
  // switch — without latching, the user:{id} topic would flip to null and churn (leave → rejoin) the person
  // group, dropping any notification in the refetch window. The person never changes within a session (a
  // relogin remounts the shell), so latching the first non-null id and never unsetting it keeps user:{id}
  // stable across a band switch — the "survives a band switch" contract, for real.
  const [stablePersonId, setStablePersonId] = useState<string>();
  useEffect(() => {
    const id = me.data?.person?.personId;
    if (id) setStablePersonId(id);
  }, [me.data]);

  useLiveGroup(stablePersonId ? `user:${stablePersonId}` : null);
  useLiveGroup(activeBandId ? `band:${activeBandId}` : null);


  if (me.isError) {
    return (
      <ShellFrame>
        <EmptyState
          title="We couldn't load your account"
          description="Check your connection and try again."
          action={<Button onClick={() => me.refetch()}>Retry</Button>}
        />
      </ShellFrame>
    );
  }

  const surfaces = nav.data?.surfaces?.filter((s) => s.visible) ?? [];
  const isPro = isProContext(bandContext);
  const navItems = buildNavItems(surfaces, nav.data?.capabilities ?? {}, isPro);
  const welcome = welcomeName(me.data, activeBandId);
  // WHO GETS THE ENVELOPE. The band-identity (owner) seat has never had one (CD: "the band primary has no
  // Messages icon at all", 2026-08-15), and since 2026-09-11 it has no /messages ROUTE either — a hidden
  // button is not a gate. `hasInbox` is the SERVER's answer, shared with the route guard so the door and the
  // gate cannot come apart; it was spelled out inline here, which is exactly how the deep link stayed open.
  const showInbox = hasInbox(me.data);

  return (
    <div className={styles.shell}>
      <ShellTopbar>
        <button
          type="button"
          className={styles.brand}
          aria-label="Artist CNTRL home"
          onClick={() => onNavigate?.("/")}
        >
          <span className={styles.brandMark}>
            <BrandMark size={30} />
          </span>
          <span className={styles.brandLockup}>
            <span className={styles.brandWordmark}>
              <Wordmark size={11} />
              {/* The Pro lockup sits BESIDE the wordmark, never replacing it — a Pro band is still an
                  Artist CNTRL band. Absent on Standard, so the bar is byte-identical to what it was. */}
              {isPro && <ProBadge size={14} />}
            </span>
            <span className={styles.brandWelcome}>
              {welcome ? (
                <>
                  Welcome back, <b>{welcome}</b>
                </>
              ) : (
                "Welcome back"
              )}
            </span>
          </span>
        </button>
        <ShellNav
          label="Primary"
          items={navItems}
          loading={nav.isLoading}
          closeKey={activeRoute}
          isActive={(route) => isTabActive(activeRoute, route)}
          onSelect={(route) => onNavigate?.(route)}
        />
        <ShellTopbarActions>
          {/* The envelope opens the inbox OVER this page rather than navigating to it (PO, 2026-09-11) —
              pressing it used to unmount whatever you were working on, and closing it dropped you on the
              dashboard. `showInbox` stays HERE: who has an inbox at all is the shell's question, and the
              same `hasInbox` the route guard enforces. */}
          {showInbox && (
            <InboxButton
              route="/messages"
              onNavigate={onNavigate}
              // The Day of Show link's address from HERE — and `activeBandId` is why the shell supplies it:
              // this route resolves its band from what is mounted, so a chat for a DIFFERENT band of yours
              // has no address here and correctly gets no link (showLink.ts).
              showHref={showHref}
            />
          )}
          <FixtureSwitchButton open={fixtureSwitchOpen} onOpen={() => setFixtureSwitchOpen(true)} />
          <NotificationBell allowPanel={isDesktop} onNavigate={onNavigate} />
          <IconButton
            icon={Settings}
            label="Settings"
            tone="outline"
            iconSize={16}
            onClick={() =>
              isDesktop && bandContext ? setSettingsOpen(true) : onNavigate?.("/settings")
            }
          />
          <BandSwitcher
            seats={me.data?.seats ?? []}
            activeBandId={activeBandId}
            personEmail={me.data?.person?.email}
            person={me.data?.person}
            onLogout={onLogout}
            onViewProfile={() => setProfileOpen(true)}
          />
        </ShellTopbarActions>
      </ShellTopbar>
      <main className={styles.content}>
        {bandContext ? (
          // A band-scoped module ALWAYS mounts under a resolved BandProvider. Never render one
          // bare — feature hooks (useBandContext/useBandQuery) require it, so a bare mount is a crash.
          <BandProvider value={bandContext}>{children}</BandProvider>
        ) : activeBandId && nav.isError ? (
          <EmptyState
            title="We couldn't load this band"
            description="Check your connection and try again."
            action={<Button onClick={() => nav.refetch()}>Retry</Button>}
          />
        ) : me.isLoading || activeBandId ? (
          // Still resolving the session (`me`) or the active band's capability document — a designed loading
          // state, never a bare (providerless) module mount. NB: while `me` loads, activeBandId
          // is briefly undefined, so this MUST also cover me.isLoading or a band-scoped route flashes a crash.
          <div className={styles.contentLoading} role="status" aria-live="polite">
            <Spinner size={22} label="Loading your band…" />
          </div>
        ) : (
          // Session resolved with no band at all (org-home) — the module decides (useBandContextOrNull).
          children
        )}
      </main>
      {/* The dev fixture switcher. At ROOT level like every other modal here — `Modal` does not portal and
          the topbar is a stacking context, so mounting it in the actions row traps it inside the nav bar. */}
      {fixtureSwitchOpen && (
        <FixtureSwitchDialog
          onClose={() => setFixtureSwitchOpen(false)}
          onNavigate={(to) => onNavigate?.(to)}
        />
      )}
      {/* "View my profile" — in place, no route (PO, 2026-08-20; the routed /profile stays for
          bookmarks). The SettingsModal precedent below: band-scoped hooks need the provider, so the
          crew-profile form mounts under one; a session with NO band falls back to the plain
          person-record editor, which is person-scoped and needs none. */}
      {profileOpen &&
        (bandContext ? (
          <BandProvider value={bandContext}>
            <MyProfileModal onClose={() => setProfileOpen(false)} />
          </BandProvider>
        ) : (
          <ProfileModal onClose={() => setProfileOpen(false)} />
        ))}
      {/* Inside the BandProvider, exactly like the routed page: the tab bodies are band-scoped and their
          hooks require it. Mounted bare it crashes the moment the gear is clicked. */}
      {settingsOpen && bandContext && (
        <BandProvider value={bandContext}>
          <SettingsModal
            initialTab={settingsTab ?? undefined}
            onClose={() => {
              setSettingsOpen(false);
              setSettingsTab(null);
            }}
          />
        </BandProvider>
      )}
    </div>
  );
}

/** Bare shell frame used for the top-level error state (no nav/context available). */
function ShellFrame({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  return (
    <div className={styles.shell}>
      <ShellTopbar>
        <div className={styles.brand}>
          <Wordmark size={22} />
        </div>
        <ShellTopbarActions>
          <Button variant="ghost" size="sm" onClick={() => void auth.logout()}>
            Log out
          </Button>
        </ShellTopbarActions>
      </ShellTopbar>
      <main className={styles.content}>{children}</main>
    </div>
  );
}
